import type { RiskLimitField, RiskLimits } from '../limits';
import type { AdvancedRuleFilter } from '../rules';
import type { LabRiskSettingsTab, RiskRuleDialogState, RiskRuleDraft } from './types';

export type LabRiskSettingsAction =
    | { type: 'SET_FIRM'; firmId: string | undefined }
    | { type: 'SET_ACTIVE_TAB'; tab: LabRiskSettingsTab }
    | { type: 'SET_DEFAULT_ENABLED'; field: RiskLimitField; enabled: boolean }
    | { type: 'SET_DEFAULT_VALUE'; field: RiskLimitField; value: number | null }
    | { type: 'SYNC_SAVED_DEFAULTS'; defaults: RiskLimits; resetDraft: boolean }
    | { type: 'START_SAVE_DEFAULTS' }
    | { type: 'APPLY_SAVED_DEFAULTS'; defaults: RiskLimits }
    | { type: 'FAIL_SAVE_DEFAULTS' }
    | { type: 'OPEN_RULE_DIALOG'; dialog: RiskRuleDialogState }
    | { type: 'CLOSE_RULE_DIALOG' }
    | { type: 'PATCH_RULE_DRAFT'; patch: Partial<RiskRuleDraft> }
    | { type: 'START_SAVE_RULE' }
    | { type: 'FAIL_SAVE_RULE'; message: string }
    | { type: 'REQUEST_DELETE_RULE'; parameterId: string; token: number }
    | { type: 'CANCEL_DELETE_RULE' }
    | { type: 'START_DELETE_RULE' }
    | { type: 'FINISH_DELETE_RULE'; token: number }
    | { type: 'SET_ADVANCED_FILTER'; filter: AdvancedRuleFilter }
    | { type: 'SET_ADVANCED_SEARCH_INPUT'; value: string };

// Initializes the firm scope on a fresh instance; the model enforces
// that the scope never changes afterwards (the owning route creates a
// new model per firm).
export const setFirm = (firmId: string | undefined): LabRiskSettingsAction => ({
    type: 'SET_FIRM',
    firmId,
});

export const setActiveTab = (tab: LabRiskSettingsTab): LabRiskSettingsAction => ({ type: 'SET_ACTIVE_TAB', tab });

export const setDefaultEnabled = (field: RiskLimitField, enabled: boolean): LabRiskSettingsAction => ({
    type: 'SET_DEFAULT_ENABLED',
    field,
    enabled,
});

export const setDefaultValue = (field: RiskLimitField, value: number | null): LabRiskSettingsAction => ({
    type: 'SET_DEFAULT_VALUE',
    field,
    value,
});

/** Refresh the server baseline; resets the draft only while the form is clean. */
export const syncSavedDefaults = (defaults: RiskLimits, resetDraft: boolean): LabRiskSettingsAction => ({
    type: 'SYNC_SAVED_DEFAULTS',
    defaults,
    resetDraft,
});

export const startSaveDefaults = (): LabRiskSettingsAction => ({ type: 'START_SAVE_DEFAULTS' });

export const applySavedDefaults = (defaults: RiskLimits): LabRiskSettingsAction => ({
    type: 'APPLY_SAVED_DEFAULTS',
    defaults,
});

export const failSaveDefaults = (): LabRiskSettingsAction => ({ type: 'FAIL_SAVE_DEFAULTS' });

export const openRuleDialog = (dialog: RiskRuleDialogState): LabRiskSettingsAction => ({
    type: 'OPEN_RULE_DIALOG',
    dialog,
});

export const closeRuleDialog = (): LabRiskSettingsAction => ({ type: 'CLOSE_RULE_DIALOG' });

export const patchRuleDraft = (patch: Partial<RiskRuleDraft>): LabRiskSettingsAction => ({
    type: 'PATCH_RULE_DRAFT',
    patch,
});

export const startSaveRule = (): LabRiskSettingsAction => ({ type: 'START_SAVE_RULE' });

export const failSaveRule = (message: string): LabRiskSettingsAction => ({ type: 'FAIL_SAVE_RULE', message });

export const requestDeleteRule = (parameterId: string, token: number): LabRiskSettingsAction => ({
    type: 'REQUEST_DELETE_RULE',
    parameterId,
    token,
});

export const cancelDeleteRule = (): LabRiskSettingsAction => ({ type: 'CANCEL_DELETE_RULE' });

export const startDeleteRule = (): LabRiskSettingsAction => ({ type: 'START_DELETE_RULE' });

// The completion carries the confirmation token it was started for so
// it can't close a different confirmation than the one that issued it.
export const finishDeleteRule = (token: number): LabRiskSettingsAction => ({
    type: 'FINISH_DELETE_RULE',
    token,
});

export const setAdvancedFilter = (filter: AdvancedRuleFilter): LabRiskSettingsAction => ({
    type: 'SET_ADVANCED_FILTER',
    filter,
});

export const setAdvancedSearchInput = (value: string): LabRiskSettingsAction => ({
    type: 'SET_ADVANCED_SEARCH_INPUT',
    value,
});
